/**
 * Affichage vidéoprojeté : lisible depuis le fond de la classe.
 * Typographie surdimensionnée, contrastes forts, aucune action possible.
 * Le mode « projection » est appliqué par la coquille (app-root).
 */

import { html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { PHASE_LABEL, retainedSeats, type RoundResult, type Seat } from '../../core/index.js';
import { StoreElement } from '../base.js';
import { pluriel, pourcentage } from '../format.js';
import { icone } from '../icons.js';

@customElement('view-affichage')
export class ViewAffichage extends StoreElement {
  private renderClassement(result: RoundResult | null, titre: string, elus: readonly string[]) {
    if (result === null) return nothing;
    const total = result.valides + result.blancs + result.nuls;
    if (total === 0) return nothing;
    const maximum = Math.max(1, ...result.classement.map((entry) => entry.voix));

    return html`
      <section class="card">
        <h2 class="t-headline">${titre}</h2>
        <p class="t-body t-muted">
          ${pluriel(result.votants, 'votant', 'votants')} ·
          ${pluriel(result.valides, 'bulletin valide', 'bulletins valides')} ·
          ${pluriel(result.blancs, 'blanc', 'blancs')} · ${pluriel(result.nuls, 'nul', 'nuls')}${result.seuil === null
            ? ' · majorité relative'
            : ` · majorité absolue à ${pluriel(result.seuil, 'voix', 'voix')}`}
        </p>
        <div>
          ${result.classement.map(
            (entry) => html`
              <div
                class="result ${elus.includes(entry.itemId) ? 'result--elected' : ''} ${result.majoritaires.includes(entry.itemId)
                  ? 'result--majoritaire'
                  : ''}"
              >
                <div class="result__head">
                  <span class="result__name">
                    ${elus.includes(entry.itemId) ? html`<span class="result__marque">${icone('checkCercle', 20)}</span>` : nothing}
                    ${entry.label}
                  </span>
                  <span class="result__voix">${entry.voix}</span>
                </div>
                <div class="result__track">
                  <div class="result__fill" style="width: ${Math.round((entry.voix / maximum) * 100)}%"></div>
                </div>
              </div>
            `,
          )}
        </div>
      </section>
    `;
  }

  private renderSieges(titre: string, sieges: readonly Seat[], accent: boolean) {
    if (sieges.length === 0) return nothing;
    const libelle = (siege: Seat): string =>
      this.store.state.persons.find((personne) => personne.id === siege.personId)?.label ?? '?';
    const titulaires = sieges.filter((siege) => siege.role === 'titulaire');
    const suppleants = sieges.filter((siege) => siege.role === 'suppleant');

    const colonne = (titreColonne: string, liste: readonly Seat[]) => html`
      <div class="stack stack--tight">
        <p class="t-label">${titreColonne}</p>
        <ul class="list">
          ${liste.length === 0
            ? html`<li class="t-body t-muted">Aucun siège pourvu</li>`
            : liste.map(
                (siege) => html`
                  <li class="list__item">
                    <span class="result__marque" aria-hidden="true">${icone('checkCercle', 22)}</span>
                    <span class="grow">${libelle(siege)}</span>
                  </li>
                `,
              )}
        </ul>
      </div>
    `;

    return html`
      <section class="card ${accent ? 'card--accent' : ''}">
        <h2 class="t-headline">${titre}</h2>
        <div class="grid-2">${colonne('Délégués titulaires', titulaires)} ${colonne('Suppléants', suppleants)}</div>
      </section>
    `;
  }

  override render() {
    const { state, computation } = this.store;
    const config = state.config;
    const siegesRetenus = retainedSeats(state, computation);
    const elus = [...computation.elusTour1, ...computation.elusTour2];
    const montrerResultats = [
      'DEPOUILLEMENT1',
      'TOUR2',
      'DEPOUILLEMENT2',
      'DEPARTAGE',
      'ACCEPTATION',
      'DESIGNATION',
      'PROCLAMATION',
    ].includes(state.phase);

    return html`
      <div class="page page--wide">
        <section class="card card--accent">
          <p class="t-label">${PHASE_LABEL[state.phase]}</p>
          <h1 class="t-display">${config?.nom ?? 'Élection'}</h1>
          <p class="t-headline">
            ${config?.classe ?? ''}${config?.etablissement ? ` — ${config.etablissement}` : ''}
          </p>
        </section>

        <div class="stats">
          <div class="stat">
            <div class="stat__value">${computation.round1.inscrits}</div>
            <div class="stat__label">Inscrits</div>
          </div>
          <div class="stat">
            <div class="stat__value">${computation.round1.votants}</div>
            <div class="stat__label">Votants au premier tour</div>
          </div>
          <div class="stat">
            <div class="stat__value">${pourcentage(computation.round1.votants, computation.round1.inscrits)}</div>
            <div class="stat__label">Participation</div>
          </div>
          <div class="stat">
            <div class="stat__value">${computation.round1.blancs + computation.round1.nuls}</div>
            <div class="stat__label">Blancs et nuls</div>
          </div>
        </div>

        ${montrerResultats
          ? html`
              ${this.renderClassement(computation.round1, 'Résultats du premier tour', elus)}
              ${this.renderClassement(computation.round2, 'Résultats du second tour', elus)}
              ${computation.ambigu
                ? html`<section class="card card--warn">
                    <div class="card__head">
                      <span class="chip-icon chip-icon--alerte" aria-hidden="true">${icone('alerte', 22)}</span>
                      <h2 class="t-headline">Égalité — départage en cours</h2>
                    </div>
                    <p class="t-body">
                      L’enseignant tranche entre
                      ${computation.ambigu.itemIds
                        .map((id) => computation.items.find((item) => item.id === id)?.label ?? id)
                        .join(', ')}.
                    </p>
                  </section>`
                : nothing}
              ${this.renderSieges(
                state.phase === 'PROCLAMATION' ? 'Résultats proclamés' : 'Sièges pourvus',
                siegesRetenus,
                state.phase === 'PROCLAMATION',
              )}
            `
          : html`<section class="card card--flat">
              <p class="t-body t-muted">Les résultats s’afficheront ici dès la clôture du premier tour.</p>
            </section>`}

        <div class="row row--between no-print">
          <span class="t-body t-muted">Vue d’affichage — aucune action n’est possible depuis cet écran.</span>
          <md-text-button @click=${() => (location.hash = `#/bureau/${this.store.electionId ?? ''}`)}>
            Retour au bureau
          </md-text-button>
        </div>
      </div>
    `;
  }
}
